/**
 * Der Abgleich zwischen Geraet und Server (KONZEPT.md §15).
 *
 * Ein Durchlauf in vier Schritten, immer in dieser Reihenfolge:
 *
 * 1. **Senden**, was in der Warteschlange liegt. Angekommenes wandert ins
 *    lokale Log und wird zur Konfliktpruefung vorgemerkt.
 * 2. **Stammstand** holen, wenn er faellig ist: Artikel, Lagerorte,
 *    Einsatzstellen, Benutzer, Status. Aendert sich selten; alle
 *    fuenfzehn Minuten reicht, und wer in der Verwaltung etwas aendert, stoesst
 *    ihn sofort an.
 * 3. **Log** holen: alles ab der letzten bekannten Folgenummer. Meist leer,
 *    deshalb billig genug fuer jede Minute.
 * 4. **Zustand rechnen** fuer die beruehrten Artikel -- mit derselben reinen
 *    Funktion wie der Server -- und nachsehen, ob eine eigene Buchung beim
 *    Eintreffen schon ueberstimmt war (§15.4).
 *
 * Konflikte werden **nie** dem Benutzer vorgelegt (CLAUDE.md §3.3). Die
 * neuere Buchung gewinnt; die ueberstimmte landet als Hinweis im
 * Sync-Protokoll dieses Geraets.
 *
 * Nie zwei Durchlaeufe gleichzeitig. Wer waehrenddessen einen anstoesst,
 * bekommt direkt danach einen weiteren -- so geht keine Buchung verloren, die
 * waehrend des Sendens dazukam.
 */

import { useSyncExternalStore } from "react";
import {
  STANDARD_STATUSARTEN,
  type AbgleichAntwort,
  type Artikel,
  type Buchung,
  type BuchungenLesenAntwort,
  type Lagerort,
} from "@thw/contracts";
import { statusRegeln, zustandBerechnen } from "@thw/core-domain";
import { anfrage, geraeteKennung, ServerFehler } from "../api/klient.js";
import { aufNeueBuchung, uebertragen } from "../api/warteschlange.js";
import { db, metaLesen, metaSchreiben, type GespeicherterZustand } from "./datenbank.js";
import { konfliktFinden } from "./konflikt.js";
import { liveStarten } from "./live.js";

/** Wie alt der Stammstand werden darf, bevor er neu geholt wird. */
const STAMMSTAND_ALTER_MS = 15 * 60_000;

/** Takt, in dem ein sichtbares Geraet mit Netz nachsieht, was es Neues gibt. */
const TAKT_MS = 60_000;

/** Buchungen je Abruf des Logs. */
const LOG_STAPEL = 1000;

/**
 * Wie lange eine einzelne Anfrage dauern darf. Im Funkloch haengt eine
 * Verbindung sonst minutenlang -- und mit ihr die Anzeige „Synchronisiere …".
 */
const ZEITLIMIT_MS = 30_000;

/** Ab welcher Abweichung der Geraeteuhr gewarnt wird (KONZEPT.md §15.6). */
export const UHR_TOLERANZ_MS = 2 * 60_000;

/** Gelesene Hinweise verschwinden nach dieser Zeit aus dem Sync-Protokoll. */
const HINWEIS_AUFBEWAHRUNG_MS = 30 * 24 * 60 * 60_000;

// --- Laufzustand fuer die Anzeige -------------------------------------------

export interface Laufzustand {
  laeuft: boolean;
  /**
   * Hat der Server zuletzt geantwortet? `navigator.onLine` allein luegt gern:
   * Ein Handy mit WLAN, aber ohne Weg zum Server, haelt sich fuer online.
   */
  erreichbar: boolean;
  /** Was beim letzten Durchlauf schiefging -- ausser fehlendem Netz. */
  fehler: string | null;
  /** Beim Laden des Logs: wie viele Buchungen in diesem Durchlauf schon da sind. */
  geladen: number;
  online: boolean;
}

let lauf: Laufzustand = {
  laeuft: false,
  erreichbar: true,
  fehler: null,
  geladen: 0,
  online: globalThis.navigator.onLine,
};
const beobachter = new Set<() => void>();

function setzen(teil: Partial<Laufzustand>): void {
  lauf = { ...lauf, ...teil, online: globalThis.navigator.onLine };
  for (const b of beobachter) b();
}

export function useLaufzustand(): Laufzustand {
  return useSyncExternalStore(
    (b) => {
      beobachter.add(b);
      return () => beobachter.delete(b);
    },
    () => lauf,
  );
}

// --- Durchlauf -------------------------------------------------------------

let aktiv: Promise<void> | null = null;
let nochmal = false;
let stammErzwingen = false;

function nochmalGewuenscht(): boolean {
  return nochmal;
}

/**
 * Stoesst einen Abgleich an und kehrt zurueck, wenn er fertig ist.
 *
 * Wirft nie: Ein gescheiterter Abgleich ist ein Zustand, den die Kopfzeile
 * zeigt, kein Fehler, den der Aufrufer behandeln muesste.
 *
 * `stammdaten: true` holt den Stammstand auch dann, wenn er noch frisch ist --
 * nach einer Aenderung in der Verwaltung oder wenn ein gescannter Code
 * unbekannt war.
 */
export function abgleichen(optionen: { stammdaten?: boolean } = {}): Promise<void> {
  if (optionen.stammdaten === true) stammErzwingen = true;
  if (aktiv !== null) {
    nochmal = true;
    return aktiv;
  }
  aktiv = (async () => {
    try {
      do {
        nochmal = false;
        await durchlauf();
        // Ueber eine Funktion gelesen: TypeScript weiss nicht, dass ein
        // anderer Aufruf `nochmal` waehrend des Wartens gesetzt haben kann,
        // und hielte die Bedingung sonst fuer immer falsch.
      } while (nochmalGewuenscht());
    } finally {
      aktiv = null;
    }
  })();
  return aktiv;
}

async function durchlauf(): Promise<void> {
  if (!globalThis.navigator.onLine) {
    setzen({ erreichbar: false });
    return;
  }
  setzen({ laeuft: true, geladen: 0 });

  try {
    const gesendet = await uebertragen();

    let alleNeu = false;
    if (stammErzwingen || (await stammstandFaellig())) {
      stammErzwingen = false;
      await stammstandHolen();
      alleNeu = true;
    }

    const geholt = await logHolen();
    const beruehrt = new Set([...gesendet, ...geholt]);

    // Buchungen zu Artikeln, die das Geraet nicht kennt: Dann ist seit dem
    // letzten Stammabruf etwas angelegt worden.
    if (!alleNeu && beruehrt.size > 0) {
      const bekannt = await db.artikel.bulkGet([...beruehrt]);
      if (bekannt.includes(undefined)) {
        await stammstandHolen();
        alleNeu = true;
      }
    }

    await zustaendeRechnen(alleNeu ? null : beruehrt);
    await konflikteSuchen();
    await metaSchreiben("letzter_abgleich", new Date().toISOString());
    setzen({ erreichbar: true, fehler: null });
  } catch (fehler) {
    if (istNetzfehler(fehler)) {
      // Kein Fehler im Sinne der Anzeige: Das Geraet ist offline, und das ist
      // hier die Grundannahme (CLAUDE.md §3.3).
      setzen({ erreichbar: false });
    } else {
      setzen({ erreichbar: true, fehler: fehlertext(fehler) });
    }
  } finally {
    setzen({ laeuft: false, geladen: 0 });
  }
}

function istNetzfehler(fehler: unknown): boolean {
  // `fetch` wirft TypeError, wenn keine Verbindung zustande kommt; das
  // Zeitlimit wirft TimeoutError. Ein vorgeschalteter Apache meldet 502 bis
  // 504, wenn der Dienst dahinter nicht laeuft -- auch das ist "nicht
  // erreichbar", nicht "kaputt".
  if (fehler instanceof TypeError) return true;
  if (fehler instanceof DOMException) return true;
  return fehler instanceof ServerFehler && fehler.code >= 502 && fehler.code <= 504;
}

function fehlertext(fehler: unknown): string {
  if (fehler instanceof ServerFehler && fehler.code === 401) {
    return "Die Anmeldung ist abgelaufen. Bitte neu anmelden — nichts geht verloren.";
  }
  return fehler instanceof Error ? fehler.message : String(fehler);
}

// --- Schritte --------------------------------------------------------------

async function stammstandFaellig(): Promise<boolean> {
  const zuletzt = await metaLesen("stammstand_am");
  return zuletzt === null || Date.now() - Date.parse(zuletzt) > STAMMSTAND_ALTER_MS;
}

/**
 * Holt den Stammstand und ersetzt die lokale Kopie in **einer** Transaktion.
 *
 * Nebenbei misst er die Uhr: Die Konfliktaufloesung haengt an der Uhrzeit
 * des Geraets (§15.6), und eine falsch gehende Uhr faellt sonst niemandem auf.
 */
async function stammstandHolen(): Promise<void> {
  const vorher = Date.now();
  const stand = await anfrage<AbgleichAntwort>(
    "GET",
    "/api/core/abgleich",
    undefined,
    ZEITLIMIT_MS,
  );
  const nachher = Date.now();
  const abweichung = Date.parse(stand.stand) - Math.round((vorher + nachher) / 2);

  await db.transaction(
    "rw",
    [
      db.artikel,
      db.lagerorte,
      db.einsatzstellen,
      db.benutzer,
      db.statusArten,
      db.meta,
      db.log,
      db.pruefen,
      db.zustaende,
    ],
    async () => {
      // Der Server kennt weniger Buchungen als das Geraet: Er ist neu
      // aufgesetzt worden (etwa ein Vorfuehrstand nach `pnpm seed`). Dann
      // passt das lokale Log nicht mehr, und der Abruf ab der alten Nummer
      // braechte nie wieder etwas. Die Warteschlange bleibt -- sie ist das
      // Einzige, was es nur hier gibt.
      const lokal = (await metaLesen("folge_stand")) ?? 0;
      if (stand.folge_stand < lokal) {
        await db.log.clear();
        await db.pruefen.clear();
        await db.zustaende.clear();
        await metaSchreiben("folge_stand", 0);
      }

      await db.artikel.clear();
      await db.artikel.bulkPut(stand.artikel.map((a) => a.artikel));
      await db.lagerorte.clear();
      await db.lagerorte.bulkPut(stand.lagerorte);
      await db.einsatzstellen.clear();
      await db.einsatzstellen.bulkPut(stand.einsatzstellen);
      await db.benutzer.clear();
      await db.benutzer.bulkPut(stand.benutzer);
      await db.statusArten.clear();
      await db.statusArten.bulkPut(stand.status_arten);
      await metaSchreiben("basis_adresse", stand.basis_adresse);
      await metaSchreiben("uhr_abweichung_ms", abweichung);
      await metaSchreiben("stammstand_am", new Date().toISOString());
    },
  );
}

/** Holt das Log ab der letzten bekannten Folgenummer. Liefert die beruehrten Artikel. */
async function logHolen(): Promise<Set<string>> {
  let seit = (await metaLesen("folge_stand")) ?? 0;
  const beruehrt = new Set<string>();
  let geladen = 0;

  for (;;) {
    const antwort = await anfrage<BuchungenLesenAntwort>(
      "GET",
      `/api/core/buchungen?seit_folge=${String(seit)}&limit=${String(LOG_STAPEL)}`,
      undefined,
      ZEITLIMIT_MS,
    );
    if (antwort.buchungen.length > 0) {
      // Log und Stand zusammen: Bricht die Verbindung mitten im Abruf ab,
      // faengt der naechste genau dort wieder an.
      await db.transaction("rw", db.log, db.meta, async () => {
        await db.log.bulkPut(antwort.buchungen);
        await metaSchreiben("folge_stand", antwort.folge_stand);
      });
      for (const b of antwort.buchungen) beruehrt.add(b.artikel_id);
      geladen += antwort.buchungen.length;
      setzen({ geladen });
    }
    seit = antwort.folge_stand;
    if (!antwort.weitere) break;
  }

  return beruehrt;
}

/**
 * Rechnet den Zustand aus dem Log -- fuer die genannten Artikel oder, mit
 * `null`, fuer alle.
 *
 * Immer aus dem **ganzen** Verlauf eines Artikels, nie „alter Zustand plus neue
 * Buchung": Eine nachgereichte Buchung kann aelter sein als das, was schon im
 * Log steht. Genau so macht es der Server auch.
 */
async function zustaendeRechnen(ids: Set<string> | null): Promise<void> {
  if (ids !== null && ids.size === 0) return;

  const arten = await db.statusArten.toArray();
  const regeln = statusRegeln(arten.length > 0 ? arten : STANDARD_STATUSARTEN);

  const artikel =
    ids === null
      ? await db.artikel.toArray()
      : (await db.artikel.bulkGet([...ids])).filter((a) => a !== undefined);
  const log =
    ids === null
      ? await db.log.toArray()
      : await db.log
          .where("artikel_id")
          .anyOf([...ids])
          .toArray();

  const jeArtikel = new Map<string, Buchung[]>();
  for (const b of log) {
    const liste = jeArtikel.get(b.artikel_id);
    if (liste === undefined) jeArtikel.set(b.artikel_id, [b]);
    else liste.push(b);
  }

  const neu: GespeicherterZustand[] = artikel.map((a) => ({
    ...zustandBerechnen({ id: a.id, art: a.art }, jeArtikel.get(a.id) ?? [], regeln),
    artikel_id: a.id,
  }));

  await db.transaction("rw", db.zustaende, async () => {
    if (ids === null) await db.zustaende.clear();
    await db.zustaende.bulkPut(neu);
  });
}

/**
 * War eine eigene Buchung beim Eintreffen schon ueberstimmt? (§15.4)
 *
 * Geprueft wird erst, wenn das Log bis zu ihrer Folgenummer vorliegt -- vorher
 * waere die Antwort geraten.
 */
async function konflikteSuchen(): Promise<void> {
  const offen = await db.pruefen.toArray();
  const stand = (await metaLesen("folge_stand")) ?? 0;
  const geraet = geraeteKennung();

  for (const pruefung of offen) {
    if (pruefung.folge > stand) continue;

    const eigene = await db.log.get(pruefung.id);
    const artikel = eigene === undefined ? undefined : await db.artikel.get(eigene.artikel_id);
    // Artikel noch nicht in der lokalen Kopie: beim naechsten Mal.
    if (eigene !== undefined && artikel === undefined) continue;

    if (eigene !== undefined && artikel !== undefined) {
      const log = await db.log.where("artikel_id").equals(artikel.id).toArray();
      const gewinner = konfliktFinden({ id: artikel.id, art: artikel.art }, eigene, log, geraet);
      if (gewinner !== null) {
        await db.hinweise.put({
          id: eigene.id,
          artikel_id: artikel.id,
          buchung: eigene,
          gewinner,
          erkannt_am: new Date().toISOString(),
          gelesen: 0,
        });
      }
    }
    await db.pruefen.delete(pruefung.id);
  }

  const grenze = new Date(Date.now() - HINWEIS_AUFBEWAHRUNG_MS).toISOString();
  await db.hinweise
    .where("gelesen")
    .equals(1)
    .filter((h) => h.erkannt_am < grenze)
    .delete();
}

/**
 * Geaenderte Stammdaten von Artikeln sofort in die lokale Kopie -- etwa nach
 * „Stammplatz setzen“, das der Server gerade beantwortet hat (ADR 0015).
 *
 * Sonst stuende der neue Stammplatz erst nach dem naechsten Stammabruf da.
 * Der wird trotzdem angestossen: Lief gerade einer, koennte er den alten
 * Stand darueberschreiben.
 */
export async function artikelUebernehmen(artikel: readonly Artikel[]): Promise<void> {
  if (artikel.length > 0) await db.artikel.bulkPut([...artikel]);
  void abgleichen({ stammdaten: true });
}

/**
 * Ein eben angelegtes Packstueck sofort in die lokale Kopie -- Lagerort und
 * Artikel (ADR 0016). Bis der Abgleich die Einbuchung bringt, haengt es an
 * seinem Standort, weil es dort in den Stammdaten steht.
 */
export async function packstueckUebernehmen(lagerort: Lagerort, artikel: Artikel): Promise<void> {
  await db.transaction("rw", db.lagerorte, db.artikel, async () => {
    await db.lagerorte.put(lagerort);
    await db.artikel.put(artikel);
  });
  void abgleichen({ stammdaten: true });
}

// --- Start -----------------------------------------------------------------

let gestartet = false;

/**
 * Haengt den Abgleich an Netz, Sichtbarkeit und Uhr.
 *
 * Niemand soll einen Knopf druecken muessen, um seine Buchungen loszuwerden:
 * Kommt das Netz zurueck, wird die App wieder sichtbar oder entsteht eine
 * Buchung, geht es los.
 */
export function abgleichStarten(): void {
  if (gestartet) return;
  gestartet = true;

  globalThis.addEventListener("online", () => void abgleichen());
  globalThis.addEventListener("offline", () => {
    setzen({ erreichbar: false });
  });
  globalThis.document.addEventListener("visibilitychange", () => {
    if (globalThis.document.visibilityState === "visible") void abgleichen();
  });
  globalThis.setInterval(() => {
    if (globalThis.document.visibilityState === "visible") void abgleichen();
  }, TAKT_MS);
  aufNeueBuchung(() => void abgleichen());
  // Aenderungen anderer: Der Server stoesst an, geholt wird wie immer (ADR 0029).
  liveStarten((art) => void abgleichen(art === "stammdaten" ? { stammdaten: true } : {}));

  // Den Browser bitten, die lokale Datenbank nicht bei Platzmangel
  // wegzuraeumen. In der Warteschlange liegen Buchungen, die es sonst
  // nirgends gibt.
  // Aeltere Browser kennen `storage.persist` nicht; dann bleibt es beim
  // Ueblichen.
  void Promise.resolve()
    .then(() => globalThis.navigator.storage.persist())
    .catch(() => false);

  void abgleichen();
}
